// GDPR Article 7: Conditions for consent - Granular consent management
// Purpose: Allow users to view and withdraw consent for specific processing purposes
// Compliance: UK GDPR, EU GDPR, CCPA (opt-out mechanism)

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Shield, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type ConsentPurpose = "essential" | "analytics" | "marketing" | "personalization" | "profiling" | "third_party_sharing";

interface ConsentRecord {
  id: string;
  purpose: ConsentPurpose;
  consent_given: boolean;
  given_at: string;
  withdrawn_at: string | null;
  consent_text: string;
}

const CONSENT_PURPOSES = {
  essential: {
    title: "Essential Services",
    description: "Required for platform functionality - cannot be disabled",
    immutable: true,
  },
  analytics: {
    title: "Analytics & Performance",
    description: "Help us improve the platform by collecting usage statistics",
    immutable: false,
  },
  marketing: {
    title: "Marketing Communications",
    description: "Receive updates, newsletters, and promotional content",
    immutable: false,
  },
  personalization: {
    title: "Personalized Experience",
    description: "Customize content and recommendations based on your preferences",
    immutable: false,
  },
  profiling: {
    title: "Profile Matching",
    description: "Use your data (including criminal records if applicable) for job matching",
    immutable: false,
  },
  third_party_sharing: {
    title: "Share with Employers",
    description: "Share your profile with potential employers for job opportunities",
    immutable: false,
  },
};

export default function ConsentManager() {
  const [consents, setConsents] = useState<Record<ConsentPurpose, boolean>>({
    essential: true,
    analytics: false,
    marketing: false,
    personalization: false,
    profiling: false,
    third_party_sharing: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadConsents();
  }, []);

  const loadConsents = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // GDPR Article 7(1): Controller must be able to demonstrate consent
      const { data, error } = await supabase
        .from('consent_records')
        .select('*')
        .eq('user_id', user.id)
        .is('withdrawn_at', null);

      if (error) throw error;

      if (data) {
        const consentMap: Record<ConsentPurpose, boolean> = {
          essential: true,
          analytics: false,
          marketing: false,
          personalization: false,
          profiling: false,
          third_party_sharing: false,
        };

        data.forEach((record: ConsentRecord) => {
          consentMap[record.purpose] = record.consent_given;
        });

        setConsents(consentMap);
      }
    } catch (error: any) {
      console.error('Error loading consents:', error);
      toast({
        title: "Error",
        description: "Failed to load consent preferences",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateConsent = async (purpose: ConsentPurpose, granted: boolean) => {
    if (CONSENT_PURPOSES[purpose].immutable) {
      return; // Cannot modify essential consents
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (!granted) {
        // GDPR Article 7(3): Withdrawing consent
        // Update existing consent record to mark as withdrawn
        const { error: withdrawError } = await supabase
          .from('consent_records')
          .update({ withdrawn_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .eq('purpose', purpose)
          .is('withdrawn_at', null);

        if (withdrawError) throw withdrawError;

        toast({
          title: "Consent Withdrawn",
          description: `${CONSENT_PURPOSES[purpose].title} consent has been withdrawn`,
        });
      } else {
        // Grant new consent
        const { error: insertError } = await supabase
          .from('consent_records')
          .insert({
            user_id: user.id,
            purpose,
            consent_given: true,
            consent_method: "settings_ui",
            consent_text: CONSENT_PURPOSES[purpose].description,
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          });

        if (insertError) throw insertError;

        toast({
          title: "Consent Granted",
          description: `${CONSENT_PURPOSES[purpose].title} consent has been granted`,
        });
      }

      setConsents((prev) => ({ ...prev, [purpose]: granted }));
    } catch (error: any) {
      console.error('Error updating consent:', error);
      toast({
        title: "Error",
        description: "Failed to update consent preference",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          <strong>Your Privacy Rights:</strong> Under UK GDPR and Data Protection Act 2018, 
          you have the right to withdraw consent at any time. Withdrawing consent will not affect 
          the lawfulness of processing based on consent before its withdrawal.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4">
        {(Object.keys(CONSENT_PURPOSES) as ConsentPurpose[]).map((purpose) => {
          const config = CONSENT_PURPOSES[purpose];
          const isGranted = consents[purpose];

          return (
            <Card key={purpose}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{config.title}</CardTitle>
                    <CardDescription className="mt-2">
                      {config.description}
                    </CardDescription>
                  </div>
                  <Switch
                    checked={isGranted}
                    onCheckedChange={(checked) => updateConsent(purpose, checked)}
                    disabled={config.immutable || saving}
                  />
                </div>
              </CardHeader>
              {config.immutable && (
                <CardContent>
                  <Alert variant="default">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Required for platform functionality - cannot be disabled
                    </AlertDescription>
                  </Alert>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <Alert>
        <AlertDescription className="text-xs text-muted-foreground">
          <strong>Data Processing Records:</strong> All consent changes are logged with 
          timestamps and IP addresses for compliance with GDPR Article 7(1) accountability 
          requirements. You can request a copy of your consent history at any time.
        </AlertDescription>
      </Alert>
    </div>
  );
}
